import { FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatalayerService, NotificationType, TrackingEvent } from '@woolworthsnz/styleguide';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';

/**
 * Listens to `statusChanges` on the form control, debounces by 3000ms, then sends a tracking event with the field validity:
 * ```
 * {
 *   event: 'notification_event',
 *   type: 'text_field',
 *   name: <tracking name>,
 *   value: 'success' | 'failure'
 * }
 * ```
 * @param instance Reference to the calling component so the statusChanges subscription can close onDestroy (decorate component with `@UntilDestroy()`
 * @param formGroup The form group that contains the control you want to track
 * @param fieldName The name of the control
 * @param trackingName String to pass as the `name` parameter in the tracking event
 * @param datalayerService Injected datalayer service
 */
export const trackFormField = (
	instance: any,
	formGroup: FormGroup,
	fieldName: string,
	trackingName: string,
	datalayerService: DatalayerService
): Subscription | undefined => {
	const formField = formGroup.get(fieldName);

	if (!formField) {
		return;
	}

	return formField.statusChanges
		.pipe(
			takeUntilDestroyed(instance),
			debounceTime(3000),
			distinctUntilChanged(),
			filter(() => !!formField.value)
		)
		.subscribe((status) => {
			const value = status === 'VALID' ? 'success' : 'failure';

			datalayerService.trackEvent(TrackingEvent.NotificationEvent, NotificationType.TextField, trackingName, value);
		});
};

/**
 * Listens to `valueChanges` on the form control then sends a tracking event dependent on the form value:
 * ```
 * {
 *   event: 'notification_event',
 *   type: 'radio_button',
 *   name: 'pick_<actionName>' | 'unpick_<actionName>',
 *   value: <radioText>
 * }
 * ```
 * @param instance Reference to the calling component so the statusChanges subscription can close onDestroy (decorate component with `@UntilDestroy()`
 * @param formGroup The form group that contains the control you want to track
 * @param fieldName The name of the control
 * @param actionName The name of the action the form value corresponds to
 * @param radioText The `value` in the tracking event
 * @param datalayerService Injected datalayer service
 */
export const trackFormCheckbox = (
	instance: any,
	formGroup: FormGroup,
	fieldName: string,
	actionName: string,
	radioText: string,
	datalayerService: DatalayerService
): Subscription | undefined => {
	if (!formGroup.get(fieldName)) {
		return;
	}

	formGroup
		.get(fieldName)
		?.valueChanges.pipe(takeUntilDestroyed(instance), distinctUntilChanged())
		.subscribe((value: boolean) => {
			const action = (value ? 'pick ' : 'unpick ') + actionName;

			datalayerService.trackEvent(TrackingEvent.NotificationEvent, NotificationType.RadioButton, action, radioText);
		});
};
